import { z } from 'zod';

/**
 * The member lifecycle (N9). Written by **`UserStatusService` only** — never by
 * another service, a repository call, or raw SQL.
 *
 * - `registered` — account exists, nothing else done yet;
 * - `profile_incomplete` — the profile wizard has been started but not finished;
 * - `pending_verification` — a verification case is open (its substates live on
 *   `verification_cases`/`verification_steps`, never here);
 * - `approved` — verified; the only status that reaches the main app;
 * - `rejected` — verification refused; may resubmit;
 * - `suspended` — **temporary**, enforcement-driven, restores to the status the
 *   member held before (N20);
 * - `banned` — **terminal**. Nothing transitions out of it.
 */
export const USER_STATUSES = [
  'registered',
  'profile_incomplete',
  'pending_verification',
  'approved',
  'rejected',
  'suspended',
  'banned',
] as const;

export const UserStatusSchema = z.enum(USER_STATUSES);

export type UserStatus = (typeof USER_STATUSES)[number];

/** The status a freshly registered account starts in. */
export const INITIAL_USER_STATUS: UserStatus = 'registered';

/** Statuses from which a member may still be suspended or banned. */
export function isTerminalStatus(status: UserStatus): boolean {
  return status === 'banned';
}
